import React, { useEffect } from 'react';
import { Form, App, Spin, Button } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { CONTRACTOR_UPDATE, CONTRACTOR_DELETE } from '../../../graphql/mutations/contractors';
import { CONTRACTOR } from '../../../graphql/queries/contractors';
import { ContractorForm } from '../../../components/Contractor/ContractorForm';
import CardLayout from '../../../layouts/CardLayout';
import { IContractorPhoto, ContractorFormValues } from '../../../types/contractor';

const ContractorEdit: React.FC = () => {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();

  const { loading: queryLoading, refetch } = useQuery(CONTRACTOR, {
    variables: { id },
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const contractor = data.contractor;
      form.setFieldsValue({
        ...contractor,
        logo: contractor.logo ? [{ url: contractor.logo }] : [],
        photos: contractor.photos?.map((url: string) => ({ url })) || [],
      });
    },
    onError: error => {
      message.error(error.message);
    },
  });

  const [updateContractor, { loading: updateLoading }] = useMutation(CONTRACTOR_UPDATE, {
    onCompleted: () => {
      message.success('Contractor successfully updated');
      navigate('/admin/contractors');
    },
    onError: error => {
      message.error(error.message);
    },
  });

  const [deleteContractor, { loading: deleteLoading }] = useMutation(CONTRACTOR_DELETE, {
    onCompleted: () => {
      message.success('Contractor successfully deleted');
      navigate('/admin/contractors');
    },
    onError: error => {
      message.error(error.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const onFinish = async (values: ContractorFormValues) => {
    try {
      const photos = values.photos?.map((photo: IContractorPhoto) => photo.url);
      const logo = (Array.isArray(values.logo) && values.logo[0]?.url) || '';

      await updateContractor({
        variables: {
          id,
          ...values,
          photos,
          logo,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = () => {
    modal.confirm({
      title: 'Delete Contractor',
      content: 'Are you sure you want to delete this contractor?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => deleteContractor({ variables: { id } }),
    });
  };

  if (queryLoading) {
    return <Spin size="large" />;
  }

  return (
    <CardLayout>
      <ContractorForm
        form={form}
        onFinish={onFinish}
        loading={updateLoading}
        submitText="Update Contractor"
      />
      <Button danger onClick={handleDelete} loading={deleteLoading}>
        Delete Contractor
      </Button>
    </CardLayout>
  );
};

export default ContractorEdit;
